import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { Check, Delete, Loader, WarningTriangle } from '@elfsight/icons';
import { Show } from '@elfsight/lib-utils';
import {
  ellipsisMixin,
  Icon,
  textColorMixin,
  textStyleMixin
} from '@elfsight/widget-ui';
import {
  createReadableClass,
  getColorWithAlpha,
  isTransparentColor,
  roundTo4
} from '@elfsight/widget-utils';
import type { UploadedFileType } from '../../contexts';
import { ERROR_TYPE, EXTENSION_ERROR_TYPE } from '../../form';
import { commonFieldsSettings } from '../commonFieldSettings';
import { COLOR_ERROR, LINE_HEIGHT_RATIO } from '../constants';
import type { CommonFieldProps, CustomFile } from '../types';
import { dangerousFileTypes } from './dangerous-file-types';
import { getFileErrorText, getNewFileList, isFileValid } from './utils';

export interface FileProps extends CommonFieldProps<CustomFile[] | null> {
  fileTypesAny: boolean;
  fileTypesAllowed?: { id: string; name: string }[];
  extensionErrorMessage?: string;
  emptyFileErrorMessage?: string;
  fileSizeExceededErrorMessage?: string;
  unexpectedErrorMessage?: string;
  dropFileText?: string;
  chooseFileText?: string;
  orDropFileText?: string;
}

const DRAG_EVENTS: Array<keyof HTMLElementEventMap> = [
  'drag',
  'dragstart',
  'dragend',
  'dragover',
  'dragenter',
  'dragleave',
  'drop'
];

const StatusIcon = (fileError: boolean, fileUploaded?: string) => {
  if (fileError) {
    return WarningTriangle;
  }
  if (fileUploaded) {
    return Check;
  }
  return LoaderIcon;
};

export function File({
  id,
  label,
  fileTypesAny,
  fileTypesAllowed = [],
  onChange,
  onFocus,
  onBlur,
  value,
  required,
  errorColor = COLOR_ERROR,
  fieldSpacing = 20,
  fieldLabelColor = '#000000',
  fieldFillColor = 'transparent',
  fieldActiveColor = '#197BFF',
  fieldColor = '#000000',
  fieldBorderType,
  fieldBorderWidth,
  fieldBorderRadius,
  fieldFontStyle = commonFieldsSettings.fieldFontStyle,
  extensionErrorMessage = 'This file cannot be uploaded because the file format is unsupported',
  emptyFileErrorMessage = 'This file cannot be uploaded because it is empty',
  fileSizeExceededErrorMessage = 'This file cannot be uploaded because file size limit (100 MB) is exceeded',
  unexpectedErrorMessage = 'An unexpected error occurred while uploading the file. Please try again',
  dropFileText = 'Drop file here',
  chooseFileText = 'Choose file',
  orDropFileText = 'or drop here'
}: FileProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileList, setFileList] = useState<CustomFile[]>([]);
  const [isDeleted, setIsDeleted] = useState(false);
  const [drag, setDrag] = useState(false);
  const [focused, setFocused] = useState(false);

  const fileListRef = useRef(fileList);
  fileListRef.current = fileList;

  const allowedTypes = fileTypesAllowed
    .map((type) => type.name)
    .filter(Boolean)
    .map((ext) => (ext[0] === '.' ? '' : '.') + ext.toLowerCase());

  const borderColor = getColorWithAlpha(fieldColor, 0.2);
  const borderWidth = fieldBorderType ? fieldBorderWidth : 1;
  const fillColor = isTransparentColor(fieldFillColor)
    ? getColorWithAlpha(fieldColor, 0.05)
    : fieldFillColor;

  const errorMessages = {
    extensionErrorMessage,
    fileSizeExceededErrorMessage,
    emptyFileErrorMessage,
    unexpectedErrorMessage
  };

  const checkExtension = useCallback(
    (name: string) => {
      const matches = name.match(/\.[0-9a-z]+$/i);
      const ext = matches?.[0]?.toLowerCase() ?? null;
      const isAllowed = !!ext && (fileTypesAny || allowedTypes.includes(ext));
      return !isAllowed || dangerousFileTypes.includes(ext);
    },
    [allowedTypes, fileTypesAny]
  );

  const fileItemInit = useCallback(
    (file: UploadedFileType) => {
      const newFileList = getNewFileList({
        file,
        fileList: fileListRef.current,
        checkExtension
      });

      fileListRef.current = newFileList;
      setFileList(newFileList);
    },
    [checkExtension]
  );

  const updateFiles = useCallback(
    (files: CustomFile[] | null) => {
      if (!files) return;

      const filesArray = Array.from(files);

      filesArray.forEach((file, i) => {
        setTimeout(() => {
          fileItemInit(file as unknown as UploadedFileType);
        }, i * 100);
      });
    },
    [fileItemInit]
  );

  const handleChange = useCallback(() => {
    if (!inputRef.current) {
      return;
    }
    // @ts-expect-error ...
    updateFiles(inputRef.current.files);
    inputRef.current.value = '';
  }, [updateFiles]);

  // handle drag event
  useEffect(() => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    const preventDefault = (e: Event) => {
      if (e instanceof DragEvent) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleDragEnter = () => setDrag(true);
    const handleDragLeave = () => setDrag(false);
    const handleDrop = (e: DragEvent) => {
      if (e.dataTransfer) {
        // @ts-expect-error ...
        updateFiles(e.dataTransfer.files);
      }
      setDrag(false);
    };

    DRAG_EVENTS.forEach((event) =>
      input.addEventListener(event, preventDefault)
    );
    input.addEventListener('dragover', handleDragEnter);
    input.addEventListener('dragenter', handleDragEnter);
    input.addEventListener('dragleave', handleDragLeave);
    input.addEventListener('dragend', handleDragLeave);
    input.addEventListener('drop', handleDrop);

    return () => {
      DRAG_EVENTS.forEach((event) =>
        input.removeEventListener(event, preventDefault)
      );
      input.removeEventListener('dragover', handleDragEnter);
      input.removeEventListener('dragenter', handleDragEnter);
      input.removeEventListener('dragleave', handleDragLeave);
      input.removeEventListener('dragend', handleDragLeave);
      input.removeEventListener('drop', handleDrop);
    };
  }, [inputRef, updateFiles]);

  // schedule files for deletion
  const handleRemove = (name: string) => {
    const updatedFileList = fileList.map((file) =>
      file.data.name === name ? { ...file, deleted: true } : file
    );
    setIsDeleted(true);
    setFileList(updatedFileList);
  };

  const handleFocused = useCallback(() => {
    onFocus?.();

    setFocused(true);
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    onBlur?.();

    setFocused(false);
  }, [onBlur]);

  // check if any files need to be removed
  useEffect(() => {
    let cancelled = false;
    let fileDeleted = false;
    const updatedFileList = fileList.filter((file) => {
      if (file.deleted) fileDeleted = true;
      return !file.deleted;
    });
    fileDeleted &&
      setTimeout(() => {
        if (!cancelled) {
          setFileList(updatedFileList);
          setIsDeleted(false);
        }
      }, 300);
    return () => {
      cancelled = true;
    };
  }, [fileList]);

  useEffect(() => {
    const files = fileList.filter(isFileValid).map((file) => file.data);

    if (!isDeleted) {
      // @ts-expect-error ...
      onChange?.(files.length ? files : null);
    }
  }, [fileList, isDeleted]);

  useEffect(() => {
    if (value) {
      updateFiles(value);
    }
  }, [value]);

  return (
    <Container _textStyle={fieldFontStyle}>
      <DropArea
        _borderWidth={borderWidth}
        _borderColor={borderColor}
        _borderRadius={fieldBorderRadius}
        _fillColor={fillColor}
        _activeColor={fieldActiveColor}
        _drag={drag}
        _focus={focused}
      >
        <Input
          id={id}
          title=""
          type="file"
          ref={inputRef}
          name={label}
          multiple
          accept={fileTypesAny ? undefined : allowedTypes.join(',')}
          onChange={handleChange}
          onFocus={handleFocused}
          onBlur={handleBlur}
          aria-required={required}
        />
        {drag ? (
          <Text _textColor={fieldActiveColor}>{dropFileText}</Text>
        ) : (
          <>
            <Link _textColor={fieldActiveColor}>{chooseFileText}</Link>
            <Text _textColor={fieldColor}>{orDropFileText}</Text>
          </>
        )}
      </DropArea>
      <div>
        {fileList.map((file) => {
          const { name: fileName, uploaded: fileUploaded } = file.data;

          const validationError =
            file.emptyFileError ||
            file.fileSizeExceededError ||
            file.extensionError;

          const fileError =
            validationError ||
            fileUploaded === ERROR_TYPE ||
            fileUploaded === EXTENSION_ERROR_TYPE;

          const fileStateSize = 13;
          const getFileColor = (color: string) =>
            fileError ? errorColor : color;

          return (
            <FileItem
              key={fileName}
              _spacing={fieldSpacing}
              _deleted={file.deleted}
            >
              <FileData>
                <FileState
                  fill={getFileColor(fieldActiveColor)}
                  size={fileStateSize}
                  component={StatusIcon(fileError, fileUploaded)}
                />
                <FileName _size={fileStateSize}>
                  <Name _textColor={getFileColor(fieldLabelColor)}>
                    {fileName}
                  </Name>

                  <RemoveIcon
                    size={fileStateSize}
                    component={Delete}
                    _color={fieldColor}
                    _activeColor={fieldActiveColor}
                    onClick={() => handleRemove(fileName)}
                  />
                </FileName>
              </FileData>

              <FileError
                _isError={fileError}
                _color={errorColor}
                _size={fileStateSize}
              >
                <Show when={fileError}>
                  {getFileErrorText(
                    {
                      ...file,
                      uploadError: fileUploaded === ERROR_TYPE,
                      extensionError:
                        file.extensionError ||
                        fileUploaded === EXTENSION_ERROR_TYPE
                    },
                    errorMessages
                  )}
                </Show>
              </FileError>
            </FileItem>
          );
        })}
      </div>
    </Container>
  );
}

const Name = styled.div<{
  _textColor: string;
}>`
  ${ellipsisMixin};
  ${textColorMixin};
`;

const Container = styled.div.attrs<{
  _textStyle: TextStyleValue;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'file-container'
  })
})`
  line-height: ${({ _textStyle }) =>
    roundTo4(_textStyle.fontSize * LINE_HEIGHT_RATIO)}px;
  ${textStyleMixin};

  ${({ _textStyle }) =>
    // Фикс для того, чтобы курсивный текст не обрезался при некоторых шрифтах
    _textStyle.fontStyle === 'italic' &&
    css`
      ${Name}::after {
        content: '';
        display: inline-block;
        width: 2px;
      }
    `};
`;

const Text = styled.div.attrs<{
  _textColor: string;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'file-text'
  })
})`
  display: inline-block;
  ${textColorMixin};
`;

const Link = styled(Text).attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'file-link'
  })
})`
  margin-right: 0.25em;
`;

const DropArea = styled.div.attrs<{
  _borderWidth?: number;
  _borderRadius?: number;
  _fillColor: string;
  _activeColor?: string;
  _drag?: boolean;
  _borderColor: string;
  _focus: boolean;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'file-drop-area'
  })
})`
  position: relative;
  padding-top: 32px;
  padding-bottom: 32px;
  text-align: center !important;
  transition: background-color 0.3s;
  border-radius: ${({ _borderRadius }) => _borderRadius}px;
  border: ${({ _borderWidth }) => _borderWidth}px dashed
    ${({ _borderColor }) => _borderColor};

  background-color: ${({ _drag, _focus, _fillColor }) =>
    _drag || _focus ? _fillColor : 'transparent'};

  &:hover {
    background-color: ${({ _fillColor }) => _fillColor};
  }
`;

const Input = styled.input.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'file-input'
  })
})`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 10;
`;

const FileItem = styled.div.attrs<{
  _deleted: boolean;
  _spacing: number;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'file-item'
  })
})`
  display: flex;
  flex-direction: column;
  opacity: 1;
  visibility: visible;
  transition:
    opacity 0.3s,
    visibility 0.3s;
  margin-top: 12px;

  &:first-child {
    margin-top: ${({ _spacing }) => Math.ceil(_spacing / 4)}px;
  }

  ${({ _deleted }) =>
    _deleted &&
    css`
      opacity: 0;
      visibility: visible;
    `}
`;

const FileState = styled(Icon).attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'file-state'
  })
})`
  margin: 0;
`;

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoaderIcon = styled(Loader).attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'file-loader'
  })
})`
  animation: ${rotate} 4.8s linear infinite;
`;

const FileData = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'file-data'
  })
})`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FileName = styled.div.attrs<{
  _size: number;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'file-name'
  })
})`
  max-width: calc(100% - ${({ _size }) => _size}px - 12px);
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ _size }) => _size}px;
  line-height: ${({ _size }) => roundTo4(_size * LINE_HEIGHT_RATIO)}px;
`;

const RemoveIcon = styled(Icon).attrs<{
  _color: string;
  _activeColor?: string;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'file-remove-icon'
  })
})`
  cursor: pointer;
  fill: ${({ _color }) => getColorWithAlpha(_color, 0.35)};

  &:hover {
    fill: ${({ _activeColor }) => _activeColor};
  }
`;

const FileError = styled.div.attrs<{
  _size: number;
  _color: string;
  _isError: boolean;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'file-error'
  })
})`
  font-size: ${({ _size }) => _size}px;
  line-height: ${({ _size }) => Math.ceil(_size * 1.2)}px;
  color: ${({ _color }) => _color};
  ${({ _isError }) => !_isError && 'display: none;'}
`;
